import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getMediaUrl } from '../../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANON_KEY = 'classroom_anon_id';
const getOrCreateAnonId = () => {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = `anon_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Today's Story — the curated daily recap. Premium light surface (white bg,
 * black text, soft greys). Designed deliberately distinct from the rest of
 * the dark app — this page is *the* moment, not another screen.
 */
const TodayStoryPage = () => {
  const { shareCode } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/share/classroom/${shareCode}/today`);
        setStory(res.data || null);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load today\'s story');
      } finally {
        setLoading(false);
      }
    })();
    // Attribution ping — privacy-safe: anon_id is only for dedup, no email/PII.
    // Reads ?ref=forward (source) and ?u={upload_id} (per-moment attribution
    // — sent by the BestMoment "Forward this moment" CTA so we can later
    // measure which specific photos spread).
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('ref') === 'forward' ? 'forward' : 'direct';
      const uploadId = params.get('u') || null;
      axios
        .post(`${API}/share/classroom/${shareCode}/story-view`, {
          kind: 'today',
          source,
          anon_id: getOrCreateAnonId(),
          ...(uploadId ? { upload_id: uploadId } : {}),
        })
        .catch(() => {});
    } catch { /* ignore — analytics must never break the page */ }
  }, [shareCode]);

  const onShare = async () => {
    const url = window.location.href;
    const text = `${story?.workspace?.name} — Today's Story`;
    if (navigator.share) {
      try {
        await navigator.share({ title: text, text, url });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — paste it anywhere ❤️');
    } catch {
      toast.error('Could not copy — share the link manually');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!story?.workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <div className="text-sm text-gray-500">Link not found.</div>
      </div>
    );
  }

  const { workspace, picks, date, count_total, forward_count } = story;
  const hero = picks[0];
  const supporting = picks.slice(1);

  // Social proof whisper — only when ≥3 forwards today (per spec: "never
  // show weak numbers"). 3-5 stays vague ("a few parents"); 6+ shows the
  // exact count. Below 3 → completely hidden.
  let socialProof = null;
  if (forward_count >= 6) {
    socialProof = `👀 Shared with family by ${forward_count} parents today`;
  } else if (forward_count >= 3) {
    socialProof = '👀 Shared with family by a few parents today';
  }

  if (!hero) {
    return (
      <div className="min-h-screen bg-white text-black" data-testid="today-story-empty">
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-4">
            Today's Story
          </p>
          <h1 className="text-3xl font-light tracking-tight mb-3">
            Nothing to share yet today.
          </h1>
          <p className="text-base text-gray-500 leading-relaxed mb-8">
            Check back later — your child's teacher hasn't added new moments today.
          </p>
          <Link
            to={`/c/${shareCode}`}
            className="inline-block text-sm font-semibold underline underline-offset-4"
            data-testid="today-story-back"
          >
            See past collections →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black" data-testid="today-story-page">
      <article className="max-w-md mx-auto px-5 sm:px-6 py-10 sm:py-14">
        {/* Hero image */}
        <figure
          className="relative w-full overflow-hidden mb-8 hero-fade-in"
          style={{
            aspectRatio: '4 / 5',
            borderRadius: '16px',
            boxShadow: '0 20px 60px -20px rgba(0,0,0,0.25)',
          }}
          data-testid="today-story-hero"
        >
          <img
            src={getMediaUrl(hero, { size: 'thumb_800' })}
            alt=""
            className="w-full h-full object-cover hero-zoom"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 45%)',
            }}
          />
          <figcaption className="absolute bottom-0 left-0 right-0 px-5 pb-5 pt-10 text-white">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-80 mb-1.5">
              Today in {workspace.name}
            </div>
            <div className="text-sm opacity-90">{formatDate(date)}</div>
          </figcaption>
        </figure>

        {/* Title block */}
        <header className="mb-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-3">
            🎬 Today's Story
          </p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight leading-tight text-gray-900">
            A glimpse from today in {workspace.name}.
          </h1>
          {socialProof && (
            <p
              className="mt-4 text-sm text-gray-500"
              data-testid="today-story-social-proof"
            >
              {socialProof}
            </p>
          )}
        </header>

        {/* Supporting images — max 2 */}
        {supporting.length > 0 && (
          <div
            className={`grid gap-2 mb-10 ${
              supporting.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
            }`}
            data-testid="today-story-supporting"
          >
            {supporting.map((s) => (
              <a
                key={s.upload_id}
                href={getMediaUrl(s.storage_path, null)}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden"
                style={{
                  aspectRatio: '4 / 5',
                  borderRadius: '12px',
                }}
                data-testid={`today-story-supporting-${s.upload_id}`}
              >
                <img
                  src={getMediaUrl(s, { size: 'thumb_800' })}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        )}

        {/* Primary CTA */}
        <Link
          to={`/c/${shareCode}`}
          className="block w-full text-center text-white font-semibold text-[15px] tracking-tight py-3.5 px-6 rounded-full transition-transform hover:scale-[1.015]"
          style={{ background: '#15161B' }}
          data-testid="today-story-view-all"
        >
          View all photos
          <ArrowRight size={14} weight="bold" className="inline ml-2 -mt-0.5" />
        </Link>

        {/* Share microcopy */}
        <button
          type="button"
          onClick={onShare}
          className="mt-5 w-full text-center text-sm text-gray-500 hover:text-gray-900 transition-colors flex items-center justify-center gap-2"
          data-testid="today-story-share"
        >
          <ShareNetwork size={14} weight="bold" />
          Share today's story with family ❤️
        </button>

        {count_total > picks.length && (
          <p
            className="mt-10 text-center text-[11px] uppercase tracking-[0.22em] font-bold text-gray-400"
            data-testid="today-story-meta"
          >
            Curated from {count_total} moments today
          </p>
        )}
      </article>

      {/* Animations — fade + subtle zoom on the hero, per brief */}
      <style>{`
        @keyframes heroFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroZoom {
          from { transform: scale(1.02); }
          to   { transform: scale(1); }
        }
        .hero-fade-in { animation: heroFadeIn 700ms ease-out both; }
        .hero-zoom    { animation: heroZoom 1400ms ease-out both; transform-origin: center; }
      `}</style>
    </div>
  );
};

export default TodayStoryPage;
